import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { getAuthToken } from "./auth";
import { getAwsCreds, getDefaultRegion, AwsCreds } from "./awsCredentials";
import { apiFetch } from "./apiFetch";
import { useDocumentTitle } from "./useDocumentTitle";
import BreadcrumbGroup from "@cloudscape-design/components/breadcrumb-group";
import Spinner from "@cloudscape-design/components/spinner";
import StatusIndicator from "@cloudscape-design/components/status-indicator";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Button from "@cloudscape-design/components/button";
import Header from "@cloudscape-design/components/header";
import Modal from "@cloudscape-design/components/modal";
import Flashbar from "@cloudscape-design/components/flashbar";
import EcsServiceDetail, { EcsServiceData } from "./EcsServiceDetail";
import EcsAttachProjectModal from "./EcsAttachProjectModal";
import ConfirmActionModal from "./ConfirmActionModal";
import { scaleEcsService } from "./ecsActions";

import { ecsServiceUrl } from "./awsConsoleUrls";
type ScaleAction = "stop" | "start";

export default function LiveEcsDetailPage() {
  const { clusterName, serviceName } = useParams<{ clusterName: string; serviceName: string }>();
  const [searchParams] = useSearchParams();
  const account = searchParams.get("account");
  const region = searchParams.get("region") || getDefaultRegion();

  const [service, setService] = useState<EcsServiceData | null>(null);
  useDocumentTitle(serviceName ? (clusterName ? `${clusterName} / ${serviceName}` : serviceName) : null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [attachVisible, setAttachVisible] = useState(false);
  const [flash, setFlash] = useState<{ type: "success" | "error" | "warning"; content: string } | null>(null);
  const [accountAlias, setAccountAlias] = useState("");
  const [creds, setCreds] = useState<AwsCreds | null>(null);

  // Stop/Start confirmation modal state.
  const [scaleAction, setScaleAction] = useState<ScaleAction | null>(null);
  const [scaling, setScaling] = useState(false);

  async function fetchData() {
    try {
      const token = getAuthToken();
      if (!token) throw new Error("No auth token");

      const accountsRes = await apiFetch(`/accounts`);
      const { data } = await accountsRes.json();
      const acc = data.find((a: any) => a.documentId === account);
      if (!acc) throw new Error("Account not found");

      const accCreds = getAwsCreds(acc.Alias);
      if (!accCreds) throw new Error(`No AWS credentials for account "${acc.Alias}"`);
      setAccountAlias(acc.Alias);
      setCreds(accCreds);

      const res = await apiFetch(
        `/inventory/live/ecs/${clusterName}/${serviceName}?region=${region}`,
        {
          headers: {
            "X-AWS-Access-Key-Id": accCreds.accessKeyId,
            "X-AWS-Secret-Access-Key": accCreds.secretAccessKey,
            "X-AWS-Session-Token": accCreds.sessionToken,
          },
        }
      );
      if (!res.ok) throw new Error(`Failed to fetch service: ${res.statusText}`);
      setService(await res.json());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusterName, serviceName, account, region]);

  const handleConfirmScale = async () => {
    if (!scaleAction || !clusterName || !serviceName) return;
    if (!creds) {
      setScaleAction(null);
      setFlash({ type: "warning", content: `No AWS credentials for account "${accountAlias}". Configure them in Settings.` });
      return;
    }
    const desiredCount: 0 | 1 = scaleAction === "start" ? 1 : 0;
    setScaling(true);
    try {
      await scaleEcsService({ clusterName, serviceName, region, desiredCount, creds });
      setScaleAction(null);
      setFlash({
        type: "success",
        content: scaleAction === "start"
          ? `Start requested for ${serviceName} (desired count set to 1).`
          : `Stop requested for ${serviceName} (desired count set to 0).`,
      });
      await fetchData();
    } catch (e: any) {
      setScaleAction(null);
      setFlash({ type: "error", content: e.message });
    } finally {
      setScaling(false);
    }
  };

  if (loading) return <Spinner size="large" />;
  if (error) return <StatusIndicator type="error">{error}</StatusIndicator>;
  if (!service) return null;

  const iclTag = service?.tags?.find((t) => t.key === "ICLProject");

  return (
    <SpaceBetween size="l">
      <BreadcrumbGroup
        items={[
          { text: "Live Inventory", href: "/live-inventory" },
          { text: "ECS", href: "/live-inventory/ecs" },
          { text: serviceName || "", href: "" },
        ]}
      />
      {flash && (
        <Flashbar items={[{ type: flash.type, content: flash.content, dismissible: true, onDismiss: () => setFlash(null) }]} />
      )}
      <Header
        variant="h1"
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              disabled={service.desiredCount === 0}
              onClick={() => setScaleAction("stop")}
            >
              Stop
            </Button>
            <Button
              disabled={service.desiredCount > 0}
              onClick={() => setScaleAction("start")}
            >
              Start
            </Button>
            {clusterName && serviceName && region && (
              <Button
                iconName="external"
                href={ecsServiceUrl(clusterName, serviceName, region)}
                target="_blank"
              >
                Open in AWS Console
              </Button>
            )}

            {iclTag && <Button onClick={() => setAttachVisible(true)}>Attach to Project</Button>}
          </SpaceBetween>
        }
      >
        {serviceName}
      </Header>
      <EcsServiceDetail service={service} />
      <ConfirmActionModal
        visible={scaleAction !== null}
        onDismiss={() => setScaleAction(null)}
        onConfirm={handleConfirmScale}
        loading={scaling}
        header={scaleAction === "start" ? "Start ECS service" : "Stop ECS service"}
        confirmLabel={scaleAction === "start" ? "Start" : "Stop"}
      >
        {scaleAction === "start"
          ? `This will set the desired task count of ${serviceName} to 1. One task will be started.`
          : `This will set the desired task count of ${serviceName} to 0. All running tasks will be stopped.`}
      </ConfirmActionModal>
      <Modal visible={attachVisible} onDismiss={() => setAttachVisible(false)} size="max">
        <EcsAttachProjectModal
          tagValue={iclTag?.value ?? ""}
          accountAlias={accountAlias}
          onDismiss={() => setAttachVisible(false)}
          onSuccess={(result) => {
            setAttachVisible(false);
            setFlash({ type: "success", content: `Attached stacks: ${result.imported} imported, ${result.updated} updated, ${result.errors} errors` });
          }}
          onError={(msg) => {
            setAttachVisible(false);
            setFlash({ type: "error", content: msg });
          }}
        />
      </Modal>
    </SpaceBetween>
  );
}
